/**
 * Wordings of «Учитель» v3 — family «advice» (docs/TEACHING-V3.md §5; pool specs: ../spec.ts).
 * Rules: no Latin, NO squares (the board shows them), Гамбитик speaks of himself as a boy, the child through {g:…},
 * no «молодец», no clock words; «вот эти клетки / этот фланг / сюда» only in pools with a board cue.
 *
 * How these words are glued (docs/TEACHING-V3.md §2.3):
 *  - A  v3.lead.advice / subject / plan / capture / rescue / answer / reveal / why / repeat  +  v3.idea.* / v3.themeTail.* / v3.idea.none
 *       («Давай сходим конём» + «— оттуда он смотрит в центр.»): a lead ends with the mover (or a word), never with a mark,
 *       has no dash inside (the tail brings its own), and fits ANY idea tail. Leads vary the verb and the shape —
 *       «…: ход {конём}» is kept to a minority of every pool, so the advice never sounds like «ходи сюда».
 *  - B  v3.aim.<idea>  +  v3.go.move / v3.go.capture  («Нам нужен центр» + «— значит, ходим пешкой.»);
 *  - D  v3.aim.<idea> as a sentence  +  v3.helper  («Нам нужен центр.» «Поможет пешка.»);
 *  - E  v3.q.<idea>  +  v3.helper  («Кто займёт центр?» «Пешка!») — every question asks «кто / какая фигура», so the
 *       nominative helper answers it.
 * The helper follows aims of ideas where the mover acts on itself too (develop, escape, improvePiece, kingActivity…),
 * so most helpers only name who acts («Твой выход, {конь}!», «Идёт {конь}!»); only a few say «поможет / справится».
 * An aim never names the moving piece (the go-tail or the helper names it) and has no dash inside.
 * `v3.lead.why` also explains the child's PREVIOUS move («Зачем мы так сходили?»), so it stays tense-neutral.
 */
import type { LessonWordings } from '../types.ts';

export const WORDINGS: LessonWordings = {
  // ───────────────────────── leads with the moving piece (form A / C and the special starts) ─────────────────────────

  'v3.lead.advice': [
    { t: 'Давай сходим {конём}' },
    { t: 'Предлагаю ход {конём}' },
    { t: 'Я бы пошёл {конём}' },
    { t: 'Попробуем {конём}' },
    { t: 'Смотри, какой ход есть у {коня:gen}' },
    { t: 'Пусть сейчас походит {конь}' },
    { t: 'Здесь пригодится {конь}' },
    { t: 'Двинем {коня}' },
    { t: 'Отправим {коня} в путь' },
    { t: 'Мне нравится ход {конём}' },
    { t: 'Советую сходить {конём}' },
    { t: 'Я за ход {конём}' },
    { t: 'Есть идея для {коня:gen}' },
    { t: 'Сходим-ка {конём}' },
    // «лучше всего / сильнее всего» — the book takes these only when the advice is the engine's move by a margin
    { t: 'Лучше всего сейчас сходить {конём}' },
    { t: 'Сильнее всего здесь ход {конём}' },
    { t: 'Я уверен: лучше всего идти {конём}' },
  ],

  'v3.lead.subject': [
    { t: '{Конь} просится в бой' },
    { t: '{Твой} {конь} {p:готов|готова} к делу' },
    { t: 'Сейчас героем будет {конь}' },
    { t: '{Конь} знает, что делать' },
    { t: 'У {коня:gen} есть задание' },
    { t: 'На сцену выходит {конь}' },
    { t: 'Пришло время {коня:gen}' },
    { t: '{Конь} хочет кое-что показать' },
    { t: 'Посмотрим, что умеет {конь}' },
    { t: 'Слово берёт {конь}' },
    { t: 'Теперь командует {конь}' },
    { t: 'А вот и {конь}' },
  ],

  'v3.lead.plan': [
    { t: 'По нашему плану идёт {конь}' },
    { t: 'Как мы и задумали, ходит {конь}' },
    { t: 'Наш план зовёт {коня}' },
    { t: 'Идём по плану: ход {конём}' },
    { t: 'Следующий шаг плана: ход {конём}' },
    { t: 'Наш план продолжает {конь}' },
    { t: 'Держимся плана и ходим {конём}' },
    { t: 'Теперь по плану очередь {коня:gen}' },
    { t: 'Дальше по плану {конь}' },
    { t: 'План подсказывает: ходим {конём}' },
  ],

  // the tail says whom we take (an idea tail with the target), so a capture lead names only the capturer
  'v3.lead.capture': [
    { t: 'Сейчас бьём {конём}' },
    { t: 'Съедаем {конём}' },
    { t: '{Конь} идёт на охоту' },
    { t: '{Конь} может перекусить' },
    { t: 'У {коня:gen} есть цель' },
    { t: 'Смотри, кого может съесть {конь}' },
    { t: 'Тут можно взять {конём}' },
    { t: '{Конь} кое-что {p:заметил|заметила}' },
    { t: 'Сейчас {конь} кое-кого съест' },
    { t: 'Удобный момент для {коня:gen}' },
  ],

  // the advice that saves: after a danger sentence («Соперник напал на коня!», «Шах!») AND alone (blitz, or a danger
  // the engine does not say aloud, §2.2), so every lead hints at the trouble itself. The mover may save itself, another
  // piece or the king, by stepping away, guarding, blocking or taking the attacker: no «на выручку спешит» (the mover
  // may be the piece in danger), no «защищаемся / выручаем / спасаем короля» (the danger lines say those just before).
  // Every first word differs; «спас-» in at most a quarter; «…: ход {конём}» in a few.
  'v3.lead.rescue': [
    { t: 'Спасаемся: ходим {конём}' },
    { t: 'Спасительный ход сделает {конь}' },
    { t: 'Не беда, у {коня:gen} есть ход' },
    { t: 'Без паники, сходим {конём}' },
    { t: 'Спокойно, выход есть: ход {конём}' },
    { t: 'Выручит нас ход {конём}' },
    { t: 'Из беды выведет {конь}' },
    { t: 'С угрозой справится {конь}' },
    { t: 'Угрозу снимет ход {конём}' },
    { t: 'Опасности не будет, если сходить {конём}' },
    { t: 'Рано соперник радуется: отвечаем {конём}' },
    { t: 'У соперника ничего не выйдет, пойдём {конём}' },
    { t: 'Нас так просто не возьмёшь, двинем {коня}' },
    { t: 'Всё под контролем, сходим {конём}' },
    { t: 'Бояться нечего, у нас есть ход {конём}' },
    { t: 'Мы не растеряемся, ходим {конём}' },
    { t: 'Выкрутиться поможет {конь}' },
    { t: 'Есть противоядие: ход {конём}' },
    { t: 'Ход {конём} всё исправит' },
    { t: 'Отбой тревоги: ходит {конь}' },
    { t: 'Неприятности отменяются, в дело идёт {конь}' },
  ],

  // after «Соперник вывел слона / напал на коня / занял центр…» — neutral: the opponent's move is not always a threat
  'v3.lead.answer': [
    { t: 'Отвечаем {конём}' },
    { t: 'А мы ответим {конём}' },
    { t: 'Наш ответ: ход {конём}' },
    { t: 'На это у нас есть {конь}' },
    { t: 'Что ж, тогда сходим {конём}' },
    { t: 'Посмотрим, что скажет {конь}' },
    { t: 'Тогда идёт {конь}' },
    { t: 'В ответ пойдёт {конь}' },
    { t: 'У нас готов ответ: ход {конём}' },
    { t: 'Раз так, ходим {конём}' },
  ],

  // the child did not find the treasure / the «Сам» move / a hidden arrow: no «ты не нашёл», we looked for it together
  'v3.lead.reveal': [
    { t: 'Вот он: ход {конём}' },
    { t: 'Смотри, кто тут нужен: {конь}' },
    { t: 'Раскрываю тайну: сходим {конём}' },
    { t: 'Показываю: ходит {конь}' },
    { t: 'Вместе посмотрим на ход {конём}' },
    { t: 'Вот что я придумал: ход {конём}' },
    { t: 'Ответ такой: идёт {конь}' },
    { t: 'Секрет знает {конь}' },
    { t: 'Разгадка: ход {конём}' },
    { t: 'Мы искали ход {конём}' },
  ],

  'v3.lead.why': [
    { t: 'Ход {конём} хорош' },
    { t: 'Ход {конём} нужен вот зачем' },
    { t: 'Объясню, зачем ход {конём}' },
    { t: 'Давай разберём ход {конём}' },
    { t: 'А вот почему ход {конём}' },
    { t: '{Конь} ходит не просто так' },
    { t: 'У хода {конём} есть причина' },
    { t: 'В ходе {конём} есть секрет' },
    { t: 'Смысл хода {конём} такой' },
    { t: 'Вот что даёт ход {конём}' },
  ],

  // «Совет» / «Повтори» ONLY when this move's advice was already said in words (§2.3; otherwise the engine takes an
  // ordinary lead), so these may lean on it: «напомню», «как я и говорил». Warm, never «опять / снова»; every first
  // word differs; the tail after it is the same idea in fresh words.
  'v3.lead.repeat': [
    { t: 'Напомню мой совет: ход {конём}' },
    { t: 'Повторю: ходим {конём}' },
    { t: 'Ещё раз про ход {конём}' },
    { t: 'Совет прежний: ходит {конь}' },
    { t: 'Как я и говорил, ходим {конём}' },
    { t: 'Мой совет остаётся: идём {конём}' },
    { t: 'Вернёмся к моему совету про {коня}' },
    { t: 'Стою на своём: пусть идёт {конь}' },
    { t: 'Всё {p:тот же|та же} {конь}' },
    { t: 'По-прежнему советую ход {конём}' },
    { t: 'Помнишь, я советовал ход {конём}' },
    { t: 'Подскажу ещё разок, это {конь}' },
    { t: 'Ничего не поменялось, ходит {конь}' },
    { t: 'Мысль всё та же: сходим {конём}' },
    { t: 'Хорошо, слушай: ход {конём}' },
    { t: 'Я не передумал: идём {конём}' },
  ],

  // ───────────────────────── the tail when no clear idea was found (also after a capture, a check or a rescue) ─────────────────────────

  'v3.idea.none': [
    { t: '— я проверил, так сильнее.' },
    { t: '— мне этот ход нравится больше других.' },
    { t: '— всё посчитано.' },
    { t: '— так будет надёжнее.' },
    { t: '— поверь, это хороший ход.' },
    { t: '— так нам будет удобнее.' },
    { t: '— я заглянул на несколько ходов вперёд.' },
    { t: '— так у нас всё будет в порядке.' },
    { t: '— я перебрал много ходов и выбрал этот.' },
    { t: '— мой шахматный нюх не подводит.' },
  ],

  // ───────────────────────── form B / D: «сначала зачем» (no piece named — the go-tail or the helper names it) ─────────────────────────

  'v3.aim.develop': [
    { t: 'Нашим фигурам пора в игру' },
    { t: 'Дома ещё сидят фигуры' },
    { t: 'В игре не хватает ещё одной фигуры' },
    { t: 'Не все наши фигуры в игре' },
    { t: 'Пусть в игре будет больше фигур' },
    { t: 'Выпускаем новую фигуру' },
    { t: 'Развитие продолжается', stages: [2, 5] },
    { t: 'Центру нужен ещё один стрелок', when: ['center'] },
    { t: 'Выведем фигуру, чтобы она смотрела в центр', when: ['center'] },
    { t: 'Нужна фигура, которая смотрит в центр', when: ['center'] },
  ],

  'v3.aim.centerPawn': [
    { t: 'Займём середину доски' },
    { t: 'Нам нужен центр' },
    { t: 'Центр ещё можно занять' },
    { t: 'Центр любит смелых' },
    { t: 'Отправим бойца в центр' },
    { t: 'Середина доски важнее краёв' },
    { t: 'Все дороги доски идут через центр' },
    { t: 'Поставим флажок в центре' },
  ],

  'v3.aim.fightCenter': [
    { t: 'Соперник занял центр, но мы поспорим' },
    { t: 'Пощекочем пешку соперника в центре' },
    { t: 'Потесним соперника в центре' },
    { t: 'Пешке соперника в центре скоро станет неуютно' },
    { t: 'Проверим центр соперника на прочность' },
    { t: 'Середина доски нужна и нам' },
    { t: 'В центре закипает борьба' },
    { t: 'Пешка соперника в центре ждёт гостей' },
  ],

  'v3.aim.supportCenter': [
    { t: 'Вдвоём пешкам в центре легче' },
    { t: 'Центр держится на дружбе пешек' },
    { t: 'Сделаем центр прочнее' },
    { t: 'Нашей пешке в центре нужна подмога', when: ['pawn'] },
    { t: 'Пешке в центре нужен друг', when: ['pawn'] },
    { t: 'Центр под ударом, поможем ему', when: ['pawn'] },
    { t: 'Скоро шагнём в центр', when: ['step'] },
    { t: 'Проложим путь в центр', when: ['step'] },
    { t: 'Центр займём не сразу, а с подготовкой', when: ['step'] },
  ],

  'v3.aim.openLine': [
    { t: 'Нашим фигурам нужна дорога' },
    { t: 'Выпустим фигуру из-за пешек' },
    { t: 'За пешкой заперта фигура' },
    { t: 'Откроем дорогу' },
    { t: 'Нашей фигуре тесно за пешкой' },
    { t: 'Дадим фигурам простор' },
    { t: 'Фигуре сзади не видно доски' },
    { t: 'Отопрём дверь для фигуры' },
  ],

  // the detector: a bishop / queen newly aims at the pawn next to the king, the king is still at home
  'v3.aim.aimWeakSquare': [
    { t: 'Навестим пешку у короля соперника' },
    { t: 'Посмотрим в сторону короля соперника' },
    { t: 'Возьмём на мушку пешку у короля' },
    { t: 'Король соперника ещё дома' },
    { t: 'Метим в пешку возле короля' },
    { t: 'Посмотрим, как соперник охраняет короля' },
    { t: 'Ищем слабое место у короля', stages: [2, 5] },
    { t: 'У соперника есть слабая пешка', stages: [2, 5] },
  ],

  // the mover is the last piece between the king and a rook
  'v3.aim.prepareCastle': [
    { t: 'Освободим место для рокировки' },
    { t: 'Королю мешает своя же фигура' },
    { t: 'Путь к рокировке ещё занят' },
    { t: 'Сделаем шаг к рокировке' },
    { t: 'Подумаем, где спрятать короля' },
    { t: 'Между королём и ладьёй стоит фигура' },
    { t: 'Приберёмся перед рокировкой' },
    { t: 'Король ждёт рокировки' },
  ],

  'v3.aim.connectRooks': [
    { t: 'Соединим ладьи' },
    { t: 'Между ладьями ещё кто-то стоит' },
    { t: 'Начало партии почти позади' },
    { t: 'Уберём последнюю фигуру между ладьями' },
    { t: 'Ладьи скучают друг без друга' },
    { t: 'Освободим ряд для ладей' },
    { t: 'Дебют подходит к концу', stages: [3, 5] },
  ],

  'v3.aim.attack': [
    { t: 'Пора нападать' },
    { t: 'Время для атаки' },
    { t: 'Перейдём в наступление' },
    { t: 'Сейчас кого-то напугаем' },
    { t: 'Одна фигура соперника плохо стоит', when: ['one'] },
    { t: 'Фигуре соперника пора побегать', when: ['one'] },
    { t: 'Двум фигурам соперника станет жарко', when: ['two'] },
    { t: 'Можно напугать сразу двоих', when: ['two'] },
    { t: 'Две фигуры соперника плохо стоят', when: ['two'] },
    { t: 'Выведем фигуру и нападём на ферзя', when: ['queenDevelop'] },
    { t: 'Ферзю соперника придётся убегать', when: ['queenDevelop'] },
    { t: 'Покажем ферзю, что он вышел рано', when: ['queenDevelop'] },
  ],

  'v3.aim.freeCapture': [
    { t: 'Можно поживиться' },
    { t: 'Тут есть бесплатная добыча' },
    { t: 'Кое-что можно съесть бесплатно' },
    { t: 'Соперник оставил фигуру без присмотра' },
    { t: 'Заберём то, что плохо лежит' },
    { t: 'Нас ждёт подарок' },
    { t: 'Фигуру соперника некому охранять' },
    { t: 'Бесплатный обед на доске' },
  ],

  // winMaterial: the piece IS defended — never «без защиты»
  'v3.aim.winMaterial': [
    { t: 'Есть выгодное взятие' },
    { t: 'Можно съесть с выгодой' },
    { t: 'Мы можем остаться в плюсе' },
    { t: 'Защита не спасёт фигуру соперника' },
    { t: 'Я посчитал: брать выгодно' },
    { t: 'Тут можно заработать' },
    { t: 'Даже с охраной фигуру стоит взять' },
    { t: 'Удачный размен сам просится' },
  ],

  'v3.aim.recapture': [
    { t: 'Заберём фигуру обратно' },
    { t: 'Не оставим это без ответа' },
    { t: 'Ответим взятием на взятие' },
    { t: 'Дадим сдачи' },
    { t: 'Будет поровну', when: ['even'] },
    { t: 'Меняемся по-честному', when: ['even'] },
    { t: 'Счёт сравняется', when: ['even'] },
    { t: 'Заберём обратно и ещё будем в плюсе', when: ['gain'] },
    { t: 'Ответное взятие ещё и выгодное', when: ['gain'] },
    { t: 'Вернём своё с прибавкой', when: ['gain'] },
  ],

  // the trade move is a capture (the go-tail is v3.go.capture): we START the trade, we do not «offer» it
  'v3.aim.trade': [
    { t: 'Можно разменяться' },
    { t: 'Начнём размен' },
    { t: 'Меняемся фигурами' },
    { t: 'Одинаковые фигуры можно поменять', when: ['same'] },
    { t: 'Отдадим такую же, какую возьмём', when: ['same'] },
    { t: 'Цена у фигур одинаковая', when: ['diff'] },
    { t: 'Поменяем фигуру на равную по силе', when: ['diff'] },
    { t: 'Когда фигур больше, меняться выгодно', when: ['ahead'] },
    { t: 'Сил у нас больше, пора меняться', when: ['ahead'] },
    { t: 'При перевесе упрощаем игру', stages: [3, 5], when: ['ahead'] },
  ],

  // the mover is the piece in danger; it may step in any direction (no «отступим / отойдём»)
  'v3.aim.escape': [
    { t: 'Сбережём фигуру' },
    { t: 'Уведём фигуру из-под удара' },
    { t: 'Спасём фигуру' },
    { t: 'Найдём фигуре безопасную клетку' },
    { t: 'Убежим от нападения' },
    { t: 'Уйдём туда, где не достанут' },
    { t: 'Не будем ждать, пока съедят' },
    { t: 'Нужно уходить' },
  ],

  'v3.aim.defend': [
    { t: 'Нашей фигуре нужна защита' },
    { t: 'Защитим фигуру' },
    { t: 'Не бросим свою фигуру' },
    { t: 'Нашу фигуру надо прикрыть' },
    { t: 'Нужен телохранитель' },
    { t: 'Один за всех, все за одного' },
    { t: 'Придём на помощь' },
    { t: 'Своих в беде не оставляем' },
  ],

  // the move stops the mate threat — it may guard the square rather than block a line
  'v3.aim.defendMate': [
    { t: 'Мату не бывать' },
    { t: 'Королю нужна помощь' },
    { t: 'Защитим короля от мата' },
    { t: 'Короля в обиду не дадим' },
    { t: 'Спрячем короля от беды' },
    { t: 'Не пустим соперника к королю' },
    { t: 'Позаботимся о короле' },
    { t: 'Сорвём планы соперника' },
  ],

  'v3.aim.answerCheck': [
    { t: 'Ответим на шах' },
    { t: 'Пора выручать короля' },
    { t: 'Королю нужно спасение' },
    { t: 'Выход из шаха найдётся' },
    { t: 'Того, кто шахует, можно съесть', when: ['capture'] },
    { t: 'Накажем того, кто шахует', when: ['capture'] },
    { t: 'Шах отобьём взятием', when: ['capture'] },
    { t: 'Сделаем шаг в безопасное место', when: ['king'] },
    { t: 'Шаг в сторону спасёт от шаха', when: ['king'] },
    { t: 'Уйдём на спокойную клетку', when: ['king'] },
    { t: 'Спрячем короля за фигурой', when: ['block'] },
    { t: 'Загородим короля', when: ['block'] },
    { t: 'Встанем на пути у шаха', when: ['block'] },
  ],

  'v3.aim.check': [
    { t: 'Можно объявить шах' },
    { t: 'Устроим королю соперника встряску' },
    { t: 'Поставим шах' },
    { t: 'Заставим короля соперника шевелиться' },
    { t: 'Шах собьёт соперника с толку' },
    { t: 'Напомним королю соперника о себе' },
    { t: 'Побеспокоим чужого короля' },
    { t: 'Сопернику придётся думать о короле' },
  ],

  'v3.aim.threatMate': [
    { t: 'Задумаем мат' },
    { t: 'Королю соперника становится жарко' },
    { t: 'Готовим мат' },
    { t: 'Королю соперника грозит беда' },
    { t: 'Устроим угрозу мата' },
    { t: 'Пусть соперник подумает о своём короле' },
    { t: 'Подготовим ловушку для короля' },
    { t: 'Нацелимся на короля соперника' },
  ],

  // the detector: a piece (not in the opening, not a capture) sees at least two more squares from its new place
  'v3.aim.improvePiece': [
    { t: 'Одной фигуре мало места' },
    { t: 'Переставим фигуру поудобнее' },
    { t: 'Каждой фигуре нужна хорошая клетка' },
    { t: 'Пусть фигура стоит активнее' },
    { t: 'Наша фигура заскучала' },
    { t: 'Устроим фигуре переезд' },
    { t: 'Фигура может работать лучше' },
    { t: 'Оживим фигуру' },
  ],

  'v3.aim.passedPawn': [
    { t: 'Впереди нет пешек соперника' },
    { t: 'Пешкам соперника нас не остановить' },
    { t: 'Пора бежать в ферзи' },
    { t: 'У нас есть бегун' },
    { t: 'Превращение зовёт' },
    { t: 'Шаг за шагом к превращению' },
    { t: 'Мечта о ферзе всё ближе' },
    { t: 'Проходная рвётся вперёд', stages: [5, 5] },
    { t: 'Проходная должна бежать', stages: [5, 5] },
  ],

  // the pawn may become any piece — no «ферзь» here
  'v3.aim.promotion': [
    { t: 'Сделаем новую фигуру' },
    { t: 'Остался один шаг' },
    { t: 'Сейчас случится превращение' },
    { t: 'Время для чуда' },
    { t: 'Родится новая фигура' },
    { t: 'Пора за наградой' },
    { t: 'Превращение уже рядом' },
    { t: 'Сейчас будет сюрприз' },
  ],

  'v3.aim.kingActivity': [
    { t: 'Время прятаться прошло' },
    { t: 'Идём в поход' },
    { t: 'Главному бойцу пора в путь' },
    { t: 'Не будем стоять на месте' },
    { t: 'Эндшпиль: теперь все в бой', stages: [5, 5] },
    { t: 'Подойдём поближе к центру', when: ['center'] },
    { t: 'Шагнём к центру', when: ['center'] },
    { t: 'Вперёд, навстречу сопернику', when: ['forward'] },
    { t: 'Наступаем', when: ['forward'] },
    { t: 'Подтянемся к пешкам', when: ['pawns'] },
    { t: 'Пешкам нужен присмотр', when: ['pawns'] },
  ],

  'v3.aim.restrictKing': [
    { t: 'Сожмём коробочку' },
    { t: 'Зажмём короля соперника' },
    { t: 'Меньше клеток королю соперника' },
    { t: 'Строим королю забор' },
    { t: 'Край доски ждёт короля соперника' },
    { t: 'Закроем королю ещё одну дверь' },
    { t: 'Ловим одинокого короля' },
  ],

  'v3.aim.rookOpenFile': [
    { t: 'Найдём дорогу без своих пешек' },
    { t: 'Поищем длинную дорогу' },
    { t: 'Нужна дорога совсем без пешек', when: ['open'] },
    { t: 'Тут дорога совсем пустая', when: ['open'] },
    { t: 'Открытая линия ждёт хозяина', stages: [3, 5], when: ['open'] },
    { t: 'По этой дороге видно пешку соперника', when: ['halfOpen'] },
    { t: 'Нацелимся на пешку соперника впереди', when: ['halfOpen'] },
    { t: 'Займём полуоткрытую линию', stages: [3, 5], when: ['halfOpen'] },
  ],

  // ───────────────────────── form B tails: which piece does it (after a goal AND after a statement) ─────────────────────────

  'v3.go.move': [
    { t: '— значит, ходим {конём}.' },
    { t: '— тут не обойтись без {коня:gen}.' },
    { t: '— тут поможет {конь}.' },
    { t: '— и {конь} уже в пути.' },
    { t: '— пусть сходит {конь}.' },
    { t: '— слово за {конём}.' },
    { t: '— зовём {коня}.' },
    { t: '— этим займётся {конь}.' },
    { t: '— ходим {конём}!' },
    { t: '— {конь} уже {p:готов|готова}.' },
    { t: '— поэтому идём {конём}.' },
    { t: '— пусть постарается {конь}.' },
    { t: '— и в дело вступает {конь}.' },
    { t: '— так что ходит {конь}.' },
  ],

  'v3.go.capture': [
    { t: '— бьём {конём}!' },
    { t: '— берём {конём}!' },
    { t: '— значит, съедаем {конём}.' },
    { t: '— и забираем {конём}!' },
    { t: '— {конь} уже облизывается.' },
    { t: '— хватаем {конём}!' },
    { t: '— съест {конь}!' },
    { t: '— это сделает {конь}.' },
    { t: '— {конь} сейчас подкрепится.' },
    { t: '— {конь} не упустит момент.' },
  ],

  // ───────────────────────── form D / E: the helper answers «кто?» or names who acts after an aim ─────────────────────────

  'v3.helper': [
    { t: 'Поможет {конь}.' },
    { t: 'Это работа для {коня:gen}.' },
    { t: '{Конь}!' },
    { t: 'Твой выход, {конь}!' },
    { t: 'Ход за {конём}.' },
    { t: '{Конь} тут как тут!' },
    { t: '{Конь} уже спешит!' },
    { t: 'Позовём {коня}!' },
    { t: 'Выбираем {коня}!' },
    { t: 'Смелее, {конь}!' },
    { t: 'Идёт {конь}!' },
    { t: 'Очередь {коня:gen}!' },
    { t: 'Вперёд, {конь}!' },
    { t: 'За дело возьмётся {конь}.' },
    { t: 'Действуй, {конь}!' },
  ],

  // ───────────────────────── form E: questions («кто / какая фигура») ─────────────────────────

  'v3.q.develop': [
    { t: 'Кто выйдет в игру?' },
    { t: 'Кто отправится из дома в игру?' },
    { t: 'Какая фигура проснётся сейчас?' },
    { t: 'Как думаешь, кто следующим выйдет из дома?' },
    { t: 'Угадай, кто хочет на прогулку?' },
    { t: 'Ну-ка, кто ещё не в игре?' },
    { t: 'Вопрос: кто засиделся дома?' },
    { t: 'Давай угадаем: кто проснётся теперь?' },
  ],

  'v3.q.centerPawn': [
    { t: 'Кто займёт центр?' },
    { t: 'Кто встанет в центр?' },
    { t: 'Какая фигура шагнёт в центр?' },
    { t: 'Как по-твоему, кто станет хозяином центра?' },
    { t: 'Скажи, кто сейчас двинется в центр?' },
    { t: 'Знаешь, кто пойдёт в центр?' },
    { t: 'Интересно, кто захватит середину?' },
    { t: 'Ну-ка, кто наведёт порядок в центре?' },
  ],

  'v3.q.fightCenter': [
    { t: 'Кто атакует пешку в центре?' },
    { t: 'Кто поспорит за центр?' },
    { t: 'Какая фигура нападёт на центр соперника?' },
    { t: 'Угадай, кто стукнет по чужому центру?' },
    { t: 'Подумай, кто растормошит центр соперника?' },
    { t: 'Вопрос: кто потревожит пешку в центре?' },
    { t: 'Интересно, кто подразнит пешку соперника?' },
    { t: 'Давай угадаем: кто бросит вызов центру?' },
  ],

  'v3.q.defend': [
    { t: 'Кто защитит {коня}?' },
    { t: 'Кто прикроет {коня}?' },
    { t: 'Какая фигура спасёт {твоего} {коня}?' },
    { t: 'Как думаешь, кто встанет на защиту {коня:gen}?' },
    { t: 'Скажи, кто не даст в обиду {коня}?' },
    { t: 'Знаешь, кто заступится за {коня}?' },
    { t: 'Ну-ка, кто станет охраной для {коня:gen}?' },
    { t: 'Подумай, кто выручит {коня}?' },
  ],

  'v3.q.attack': [
    { t: 'Кто нападёт на {коня}?' },
    { t: 'Кто напугает {коня}?' },
    { t: 'Какая фигура потревожит {коня}?' },
    { t: 'Угадай, кто возьмёт {коня} на прицел?' },
    { t: 'Как по-твоему, кто может атаковать {коня}?' },
    { t: 'Интересно, кто погонится за {конём}?' },
    { t: 'Вопрос: кто устроит охоту на {коня}?' },
    { t: 'А ну-ка, кто пойдёт на {коня} в атаку?' },
  ],

  'v3.q.freeCapture': [
    { t: 'Кто съест {коня}?' },
    { t: 'Кто заберёт {коня} бесплатно?' },
    { t: 'Какая фигура съест {коня} даром?' },
    { t: '{Конь} без защиты — кто {его} съест?' },
    { t: 'Как думаешь, кто полакомится {конём}?' },
    { t: 'Угадай, кто утащит {коня}?' },
    { t: 'Знаешь, кто сцапает {коня}?' },
    { t: 'Давай угадаем: кто подберёт {p:бесплатного|бесплатную} {коня}?' },
  ],

  // the target is the opponent's piece that has just taken ours — never «заберёт {коня} обратно» (it was not ours)
  'v3.q.recapture': [
    { t: 'Кто сразу заберёт {коня}?' },
    { t: 'Кто съест {коня} в ответ?' },
    { t: 'Какая фигура ответит на это взятие?' },
    { t: 'Как по-твоему, кто снимет {коня} с доски?' },
    { t: 'Скажи, кто ответит ударом на удар?' },
    { t: 'Подумай, кто накажет {коня}?' },
    { t: 'Ну-ка, кто быстро разберётся с {конём}?' },
    { t: 'Интересно, кто тут не растеряется?' },
  ],

  'v3.q.check': [
    { t: 'Кто объявит шах?' },
    { t: 'Кто может дать шах?' },
    { t: 'Какая фигура поставит шах?' },
    { t: 'Как думаешь, кто потревожит чужого короля?' },
    { t: 'Угадай, кто заставит короля соперника спасаться?' },
    { t: 'Знаешь, кто напугает короля?' },
    { t: 'Вопрос: кто постучится к королю соперника?' },
    { t: 'Интересно, кто скажет королю шах?' },
  ],

  // ───────────────────────── castling as a whole sentence (short castling; the long one below) ─────────────────────────

  'v3.whole.castle': [
    { t: 'Сделаем рокировку и спрячем короля в домик!', stages: [1, 2] },
    { t: 'Скорее в домик: рокировка!', stages: [1, 2] },
    { t: 'Давай сделаем рокировку: король спрячется, а ладья выйдет.', stages: [1, 2] },
    { t: 'Король прыгает в домик: делаем рокировку!', stages: [1, 2] },
    { t: 'Королю нужно укрытие — рокировка!', stages: [1, 2] },
    { t: 'Сейчас можно сделать рокировку — король будет в домике.', stages: [1, 2] },
    { t: 'Рокируемся: король уйдёт в укрытие, ладья войдёт в игру.', stages: [3, 5] },
    { t: 'Короткая рокировка: король в безопасности, ладья ближе к центру.', stages: [3, 5] },
    { t: 'Время рокироваться: королю укрытие, ладье работа.', stages: [3, 5] },
    { t: 'Уводим короля из центра: делаем короткую рокировку.', stages: [3, 5] },
    { t: 'Короткая рокировка сейчас очень кстати.', stages: [3, 5] },
    { t: 'Королю не место в центре — рокируемся.', stages: [3, 5] },
    { t: 'Делаем рокировку: сразу ходят король и ладья!' },
    { t: 'Прячем короля: рокировка!' },
    { t: 'Рокировка спрячет короля и выведет ладью.' },
    { t: 'Время для рокировки!' },
    { t: 'Сделаем рокировку, и король будет спокоен.' },
  ],

  'v3.whole.castleLong': [
    { t: 'Сделаем длинную рокировку: король уйдёт на сторону ферзя.' },
    { t: 'Длинная рокировка: король прячется на стороне ферзя.' },
    { t: 'Пора делать длинную рокировку — ладья сразу станет ближе к центру.' },
    { t: 'Король уходит в домик на стороне ферзя — длинная рокировка!' },
    { t: 'Рокируемся в длинную сторону: король спрячется, ладья проснётся.' },
    { t: 'Давай сделаем длинную рокировку — королю там будет спокойнее.' },
    { t: 'Длинная рокировка сразу выводит ладью в игру.' },
  ],
};
